import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/ui-bits";
import { ArrowUpRight } from "lucide-react";
import { EAR_EXCEPTION_GROUPS } from "@/data/earExceptions";

// EAR License Exceptions — top-level menu.
//
// Layout: two column-groups (List-Based, Non-List-Based), each rendered as a
// vertical stack of exception cards. Cards for exceptions with nested rules
// (TMP, RPL, GOV, TSU, APR) expose the sub-list inline so a user can jump
// directly to (say) "TMP · Tools of the Trade" without a second click.
//
// Detail pages live at /tools/ear-license-exceptions/:slug and (for now)
// carry a "checklist coming soon" placeholder — the compliance checklists
// will be layered on top of this scaffolding in a subsequent build.
export default function EARExceptions() {
  const totalCount = EAR_EXCEPTION_GROUPS.reduce(
    (n, g) => n + g.items.reduce((m, i) => m + 1 + (i.subs ? i.subs.length : 0), 0),
    0,
  );

  return (
    <Layout>
      <SEO
        title="EAR License Exceptions — Index"
        description="A browsable index of every EAR License Exception under 15 CFR Part 740 — including nested exceptions like TMP, RPL, GOV, TSU, and APR. Each entry links to a dedicated compliance page."
        path="/tools/ear-license-exceptions"
      />

      {/* Hero */}
      <section className="border-b border-hairline" data-testid="ear-exceptions-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <Eyebrow>Reference · 15 CFR Part 740</Eyebrow>
              <h1 className="display-1 text-ink mt-6 max-w-4xl">EAR License Exceptions.</h1>
              <p className="mt-6 text-ink-soft italic font-serif text-lg leading-snug max-w-2xl">
                Every License Exception on the EAR side of the export-control system, indexed and cross-linked.
              </p>
              <p className="mt-6 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
                License Exceptions authorize exports, reexports, and in-country transfers that would otherwise require a specific license from BIS. Some are keyed to the ECCN itself (List-Based); others turn on the transaction type (Non-List-Based). Certain sections — TMP, RPL, GOV, TSU, and APR — house several distinct exceptions under one roof, and each is listed separately below.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right space-y-1">
              <p className="section-num" data-testid="ear-exceptions-count">{totalCount}</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">exceptions indexed</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mt-3">
                Source · 15 CFR Part 740
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Groups */}
      {EAR_EXCEPTION_GROUPS.map((group) => (
        <section
          key={group.id}
          className={`border-b border-hairline ${group.id === "non-list-based" ? "bg-ivory-alt" : ""}`}
          data-testid={`ear-exceptions-group-${group.id}`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
            <Eyebrow>{group.label}</Eyebrow>
            <p className="mt-4 text-ink-soft font-serif italic text-lg leading-snug max-w-3xl">
              {group.kicker}
            </p>

            <ul className="mt-10 border-t border-hairline">
              {group.items.map((item) => (
                <li key={item.slug} className="border-b border-hairline">
                  <Link
                    to={`/tools/ear-license-exceptions/${item.slug}`}
                    className="group grid grid-cols-12 gap-6 py-7 items-baseline hover:bg-ivory transition-colors -mx-3 px-3"
                    data-testid={`ear-exception-card-${item.slug}`}
                  >
                    <div className="col-span-3 sm:col-span-2">
                      <p className="font-mono text-[15px] font-semibold text-brass-deep">{item.code}</p>
                      <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-ink-soft font-mono">{item.cfr}</p>
                    </div>
                    <div className="col-span-9 sm:col-span-9">
                      <h3 className="font-serif text-xl sm:text-2xl text-ink group-hover:text-brass transition-colors leading-tight">
                        {item.name}
                      </h3>
                      <p className="mt-2 text-ink-soft text-[15px] leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                    <div className="hidden sm:block col-span-1 text-right">
                      <ArrowUpRight className="w-4 h-4 inline-block text-brass opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                    </div>
                  </Link>

                  {item.subs && (
                    <ul className="pl-3 sm:pl-[16.6666%] pb-6 -mt-1">
                      {item.subs.map((sub) => (
                        <li key={sub.slug} className="border-l-2 border-brass/40 pl-4 py-2">
                          <Link
                            to={`/tools/ear-license-exceptions/${sub.slug}`}
                            className="group flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 hover:bg-ivory transition-colors -ml-2 pl-2 pr-2 py-1"
                            data-testid={`ear-exception-sub-${sub.slug}`}
                          >
                            <span className="font-mono text-[12px] font-semibold text-brass-deep whitespace-nowrap sm:w-40 shrink-0">
                              {sub.cfr.replace("15 CFR § ", "")}
                            </span>
                            <span className="text-[14px] text-ink group-hover:text-brass transition-colors leading-snug">
                              {sub.name}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </section>
      ))}

      {/* Disclaimer */}
      <section className="bg-ink text-ivory" data-testid="ear-exceptions-disclaimer">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-14 text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-4">Note</p>
          <p className="font-serif text-xl sm:text-2xl leading-snug tracking-tight">
            Descriptions summarize the regulation and are not the authoritative text. Compliance checklists for each exception are being layered in over time. Verify every use against the eCFR and consult counsel before relying on any exception.
          </p>
        </div>
      </section>
    </Layout>
  );
}
